import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useOutletContext, useSearchParams } from 'react-router-dom';
import {
  AlertTriangle,
  BookOpen,
  Check,
  EyeOff,
  FileText,
  Loader2,
  Lock,
  Plus,
  RotateCcw,
  Search,
  Send,
  Sparkles,
  Trash2,
  TrendingUp,
  Upload,
  X,
} from 'lucide-react';
import { ArticleSectionsEditor, ArticleSectionsView } from '../components/ArticleSectionsEditor.js';
import { canAccess, capabilitiesIn, hasCapability } from '../lib/access.js';
import {
  ApiError,
  api,
  type ArticleEfficacy,
  type ArticleMatches,
  type KbArticleDetail,
  type Me,
  type MiningRun,
  type QueueHealth,
  type TextReview,
} from '../lib/api.js';
import { ago, dateTime, num, pct } from '../lib/format.js';
import {
  AccessDenied,
  Badge,
  Button,
  Callout,
  CellStack,
  Dialog,
  DialogContent,
  DialogFooter,
  Field,
  Input,
  PageHeader,
  Panel,
  Select,
  Spinner,
  TBody,
  TD,
  TH,
  THead,
  TR,
  Table,
  Textarea,
  Tooltip,
  cn,
  useToast,
} from '../ui/index.js';

/**
 * Knowledge suggestions and the review cycle (FR-KB-20..45).
 *
 * The screen is the review gate. Nothing here reaches the retrieval index
 * without somebody reading it and saying so, and the thing a reviewer needs
 * in order to say so — the tickets the draft was written from — is on the
 * same screen as the draft. Approving without being shown the evidence is
 * not review.
 */

type Audience = 'external' | 'internal';

/** An article written by hand, with no mined pattern behind it. */
interface Standalone {
  id: string;
  audience: Audience;
  status: string;
  title: string;
  updated_at: string;
}

interface Pattern {
  id: string;
  problem: string;
  subject: string | null;
  kind: string;
  members: string[];
  tickets: number;
  median_elapsed_minutes: number | null;
  automated_share: string;
  priority_score: number;
  trend: 'emerging' | 'steady' | 'fading';
  status: string;
  articles: { id: string; audience: Audience; status: string; title: string }[];
}

const STATUS_TONE: Record<string, 'neutral' | 'accent' | 'info' | 'ok' | 'warn' | 'danger'> = {
  draft: 'neutral',
  in_review: 'warn',
  approved: 'info',
  publishing: 'warn',
  published: 'ok',
  rejected: 'danger',
  withdrawing: 'warn',
  /**
   * Not `neutral`: that is `draft`'s tone, and a withdrawn article read as a
   * draft is read as something that was never the answer rather than
   * something that stopped being it (FR-KB-58).
   */
  withdrawn: 'accent',
};

/** States owned by the worker, not the reader — nothing to act on until they clear. */
const IN_FLIGHT = new Set(['publishing', 'withdrawing']);

/**
 * Past this age an `ok` sweep is shown as stale rather than merely dated
 * (FR-KB-59). A timestamp alone reads as current until the reader does the
 * subtraction themselves; a list two weeks old that LOOKS live is worse than
 * one that says so.
 */
const STALE_MINING_MS = 48 * 60 * 60 * 1000;

/**
 * Captions the pattern panel with when the sweep last ran and what it found
 * (FR-KB-59). Rendered whether or not there are candidates to show, because a
 * failed or stale sweep is exactly as much of a lie sitting above a full list
 * as above an empty one.
 */
function MiningStatus({ lastRun, hasPatterns }: { lastRun: MiningRun | null; hasPatterns: boolean }) {
  // Patterns with no recorded run: they were stored before run history was
  // kept. Saying nothing at all would leave the reader assuming the list is
  // current, which is the exact confusion this caption exists to prevent.
  if (!lastRun) {
    return hasPatterns ? (
      <p className="mb-3 text-2xs text-ink-tertiary">
        Last swept: not recorded. These candidates predate sweep history, so their age is
        unknown.
      </p>
    ) : null;
  }

  const manual = lastRun.trigger === 'manual' ? ' · hand-run, not on a schedule' : '';

  if (lastRun.outcome === 'running') {
    return (
      <Callout tone="info" icon={<Loader2 className="animate-spin" />} className="mb-3">
        A sweep is in progress{manual}. The candidates below are from before it started.
      </Callout>
    );
  }

  if (lastRun.outcome === 'failed') {
    return (
      <Callout
        tone="danger"
        icon={<AlertTriangle />}
        title="The last sweep failed"
        className="mb-3"
      >
        Attempted {ago(lastRun.started_at)}{manual} &mdash; {lastRun.error ?? 'no error was recorded'}.
        The list below is whatever an earlier successful sweep found; it is not being kept
        current.
      </Callout>
    );
  }

  const at = lastRun.finished_at ?? lastRun.started_at;
  const summary = `${num(lastRun.patterns_found)} problem${lastRun.patterns_found === 1 ? '' : 's'}, ${num(lastRun.patterns_new)} new`;
  const stale = Date.now() - new Date(at).getTime() > STALE_MINING_MS;

  if (stale) {
    return (
      <Callout
        tone="warn"
        icon={<AlertTriangle />}
        title={`Stale — last swept ${ago(at)}`}
        className="mb-3"
      >
        {summary}{manual}. Nothing has run since &mdash; treat this as a snapshot, not a live list.
      </Callout>
    );
  }

  return (
    <p className="mb-3 text-2xs text-ink-tertiary">
      Swept {ago(at)} &middot; {summary}
      {manual}
    </p>
  );
}

/**
 * An article's state, including the two the worker owns.
 *
 * `publishing` and `withdrawing` get a spinner because the difference between
 * "asked for" and "done" is the whole reason those states exist: an article
 * labelled plainly `withdrawn` while the index has not confirmed removal is
 * still answering requesters.
 */
function ArticleStatusBadge({ status }: { status: string }) {
  const flight = IN_FLIGHT.has(status);
  return (
    <Badge
      tone={STATUS_TONE[status] ?? 'neutral'}
      icon={
        flight ? (
          <Loader2 className="animate-spin" />
        ) : status === 'withdrawn' ? (
          <EyeOff />
        ) : undefined
      }
      title={
        flight
          ? 'In progress — the index has not confirmed this yet.'
          : status === 'withdrawn'
            ? 'Was published, then pulled out of the index.'
            : undefined
      }
    >
      {status.replace('_', ' ')}
    </Badge>
  );
}

export function Knowledge() {
  const ctx = useOutletContext<{
    tenant: string;
    role: string;
    tenantName: string;
    // The shared type, not a local copy: a second declaration of this shape
    // is how `grants` came to be sent by the server, typed nowhere, and
    // therefore unused for the one check it exists for.
    me?: Me;
  }>();
  const [params] = useSearchParams();

  /**
   * A link from a ticket carries its own group.
   *
   * Without this the Pattern button on a ticket was a dead end twice over:
   * it pointed at a path that does not exist, and even corrected it would
   * have landed on whichever group the header happened to be showing — so a
   * pattern from an ESAtech ticket would be looked for in Human Resources
   * and quietly not found. The group travels with the link instead.
   */
  const wanted = params.get('group');
  const known = ctx.me?.tenants.find((t) => t.id === wanted);
  const tenant = known ? known.id : ctx.tenant;
  const role = known ? (known.role ?? ctx.role) : ctx.role;
  const tenantName = known ? (known.name ?? known.id) : ctx.tenantName;
  const elsewhere = Boolean(known) && known!.id !== ctx.tenant;

  const focus = params.get('pattern');
  const allowed = canAccess(role, 'knowledge', capabilitiesIn(ctx.me, tenant));
  const [openArticle, setOpenArticle] = useState<string | null>(null);
  const [writing, setWriting] = useState(false);
  // As the API decides it (/me capabilities), the desk's own role choices
  // included — see hasCapability.
  const canWrite = hasCapability(ctx.me, tenant, 'kb.author');
  const perms = {
    author: canWrite,
    review: hasCapability(ctx.me, tenant, 'kb.review'),
    publish: hasCapability(ctx.me, tenant, 'kb.publish'),
  };

  const q = useQuery({
    // Same reason as the article dialog: a card labelled "Publishing" is
    // wrong within seconds, and the reader is usually looking straight at it.
    refetchInterval: (query) =>
      query.state.data?.standalone?.some((a) => IN_FLIGHT.has(a.status)) ? 2_000 : false,
    queryKey: ['kb-patterns', tenant],
    queryFn: () =>
      api.get<{ patterns: Pattern[]; lastRun: MiningRun | null; standalone: Standalone[] }>(
        `/api/v1/tenants/${tenant}/kb/patterns?limit=60`,
      ),
    enabled: Boolean(tenant) && allowed,
  });

  const eff = useQuery({
    queryKey: ['kb-efficacy', tenant],
    queryFn: () =>
      api.get<{ articles: ArticleEfficacy[] }>(`/api/v1/tenants/${tenant}/kb/efficacy?limit=50`),
    enabled: Boolean(tenant) && allowed,
  });

  if (!allowed) return <AccessDenied area="knowledge" role={role} tenantName={tenantName} />;

  const patterns = q.data?.patterns ?? [];
  const lastRun = q.data?.lastRun ?? null;
  const withArticles = patterns.filter((p) => p.articles.length > 0).length;
  const published = patterns.reduce(
    (n, p) => n + p.articles.filter((a) => a.status === 'published').length,
    0,
  );

  return (
    <div className="p-6">
      <PageHeader
        title="Knowledge"
        description={`${tenantName} · problems the desk keeps solving, and what has been written up`}
        actions={
          canWrite ? (
            <Button onClick={() => setWriting(true)}>
              <Plus aria-hidden className="h-3.5 w-3.5" />
              Write an article
            </Button>
          ) : undefined
        }
      />

      {writing && (
        <NewArticleDialog
          tenant={tenant}
          onClose={() => setWriting(false)}
          onCreated={(id) => {
            setWriting(false);
            setOpenArticle(id);
          }}
        />
      )}

      {elsewhere && (
        <Callout tone="info" className="mb-4">
          Showing <strong>{tenantName}</strong> because you followed a link from one of its
          tickets. Switch group in the header to see your own.
        </Callout>
      )}

      <Panel
        title="Suggested from your tickets"
        subtitle="Ranked by how much work each one still costs a person"
        footer={
          <p className="text-2xs leading-relaxed text-ink-tertiary">
            Ordering is a <strong className="text-ink-secondary">relative score</strong>, not
            hours: volume × median elapsed time × the share still needing a person. Elapsed is
            not effort — the imported tickets record no time spent, so real desk cost is
            unknown and is not implied here.
          </p>
        }
      >
        {q.isLoading && <Spinner />}
        {q.error && <p className="text-xs text-ink-tertiary">Suggestions could not be loaded.</p>}

        {q.data && <MiningStatus lastRun={lastRun} hasPatterns={patterns.length > 0} />}

        {/* `lastRun === null` is a job that has never run; zero patterns from
            a real sweep is an answer. The two must not share this wording —
            `MiningStatus` already covers the latter. */}
        {q.data && patterns.length === 0 && lastRun === null && (
          <p className="py-8 text-center text-xs text-ink-tertiary">
            No patterns mined yet. Run the miner over this group&rsquo;s resolved tickets and
            the candidates appear here.
          </p>
        )}

        {patterns.length > 0 && (
          <>
            <p className="mb-3 text-2xs text-ink-tertiary">
              {num(patterns.length)} problems · {num(withArticles)} drafted · {num(published)}{' '}
              published
            </p>
            <ul className="space-y-2">
              {patterns.map((p) => (
                <PatternRow
                  key={p.id}
                  p={p}
                  tenant={tenant}
                  onOpen={setOpenArticle}
                  focused={p.id === focus}
                />
              ))}
            </ul>
          </>
        )}

        {/* WRITTEN BY HAND. These hang off no pattern, so the list above
            cannot carry them -- it walks patterns and joins articles. Kept in
            the same panel rather than a separate one because to the reader
            they are the same thing: written-up answers. */}
        {(q.data?.standalone ?? []).length > 0 && (
          <div className="mt-5 border-t border-line-subtle pt-4">
            <p className="mb-2 text-2xs text-ink-tertiary">
              {num((q.data?.standalone ?? []).length)} written by hand
            </p>
            <ul className="space-y-1.5">
              {(q.data?.standalone ?? []).map((a) => (
                <li key={a.id}>
                  <button
                    type="button"
                    onClick={() => setOpenArticle(a.id)}
                    className={cn(
                      'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left',
                      'transition-colors hover:bg-surface-hover',
                    )}
                  >
                    <FileText aria-hidden className="h-3.5 w-3.5 shrink-0 text-ink-tertiary" />
                    <span className="min-w-0 flex-1 truncate text-xs text-ink">{a.title}</span>
                    <Badge tone={STATUS_TONE[a.status] ?? 'neutral'}>{a.status}</Badge>
                    <span className="shrink-0 text-3xs text-ink-tertiary">
                      {a.audience === 'external' ? 'Requesters' : 'Agents'}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Arrived from a ticket for a pattern that is not in this list. The
            alternative is a page that looks complete and silently omits the
            one row the reader came for. */}
        {focus && q.data && !patterns.some((p) => p.id === focus) && (
          <p className="mt-3 text-xs text-warn">
            That pattern is no longer among {tenantName}&rsquo;s candidates — it may have been
            dismissed, or re-mined under a different problem statement.
          </p>
        )}
      </Panel>

      <Panel
        title="Article efficacy"
        subtitle="Whether a served article actually solved the problem, not just that it was shown"
        className="mt-6"
        flush={(eff.data?.articles.length ?? 0) > 0}
        footer={
          <div className="space-y-1 text-2xs leading-relaxed text-ink-tertiary">
            <p>
              <strong className="text-ink-secondary">Deflection rate</strong> is over decided
              auto-response outcomes only — <strong className="text-ink-secondary">pending</strong>{' '}
              servings still inside their reopen window are excluded from it, not counted as a
              success. A dash there means nothing has been decided yet, not a zero.
            </p>
            {/* The other reason for a dash, and the reason one article can
                appear twice. Both were read as broken data before the row
                said which population it describes. */}
            <p>
              An article <strong className="text-ink-secondary">answered a requester</strong> and
              one <strong className="text-ink-secondary">shown to an agent</strong> are measured
              differently and are listed separately, so an article used both ways appears twice.
              A dash in a column means that measure does not apply to that row — only an
              auto-response can be deflected, and only a suggestion can be used or ignored.
            </p>
          </div>
        }
      >
        {eff.isLoading && <Spinner />}
        {eff.error && (
          <p className="text-xs text-ink-tertiary">Efficacy could not be loaded.</p>
        )}

        {/* Nothing served is a different fact from nothing having worked —
            the latter would still be a full table of zeroes and low rates.
            Collapsing the two into one empty table reads as "the desk has
            never solved anything", which is not what an empty result means. */}
        {eff.data && eff.data.articles.length === 0 && (
          <p className="py-8 text-center text-xs text-ink-tertiary">
            No article has been served to a ticket yet — nothing to measure.
          </p>
        )}

        {eff.data && eff.data.articles.length > 0 && (
          <Table minWidth={720}>
            <THead>
              <TR>
                <TH>Article</TH>
                <TH numeric>Served</TH>
                <TH numeric>Deflected</TH>
                <TH numeric>Reopened</TH>
                <TH numeric>Escalated</TH>
                <TH numeric>Used</TH>
                <TH numeric>Ignored</TH>
                <TH numeric title="Still inside the reopen window — no outcome yet.">
                  Pending
                </TH>
                <TH numeric>Deflection rate</TH>
              </TR>
            </THead>
            <TBody>
              {eff.data.articles.map((a) => (
                <ArticleEfficacyRow key={`${a.article_id}:${a.mode}`} a={a} />
              ))}
            </TBody>
          </Table>
        )}
      </Panel>

      {perms.publish && <StrandedArticles tenant={tenant} />}
      {perms.publish && <DeletedArticles tenant={tenant} />}

      {openArticle && (
        <ArticleReview
          tenant={tenant}
          id={openArticle}
          perms={perms}
          onClose={() => setOpenArticle(null)}
        />
      )}
    </div>
  );
}

interface DeletedArticle {
  id: string;
  title: string;
  status: string;
  audience: 'internal' | 'external';
  deleted_at: string;
  delete_reason: string | null;
  deleted_by_name: string | null;
}

/**
 * Deleted articles (071): hidden everywhere, kept whole, and put back from
 * here. Folded to its count; absent when there are none.
 */
function DeletedArticles({ tenant }: { tenant: string }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const q = useQuery({
    queryKey: ['kb-deleted', tenant],
    queryFn: () => api.get<{ articles: DeletedArticle[] }>(`/api/v1/tenants/${tenant}/kb/articles/deleted`),
  });
  const restore = useMutation({
    mutationFn: (id: string) => api.post(`/api/v1/tenants/${tenant}/kb/articles/${id}/restore`, {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['kb-deleted', tenant] });
      void qc.invalidateQueries({ queryKey: ['kb-patterns', tenant] });
      toast.success('Article restored', 'It is back exactly as it was, in the same state.');
    },
    onError: (e: unknown) => toast.error('Not restored', e instanceof ApiError ? e.message : undefined),
  });
  const rows = q.data?.articles ?? [];
  if (rows.length === 0) return null;
  return (
    <Panel
      className="mt-6"
      title={
        <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open} className="text-left">
          Deleted articles <span className="text-2xs font-normal text-ink-tertiary">· {rows.length}</span>
        </button>
      }
      subtitle="Hidden from everything, kept whole. Restore puts one back exactly as it was."
    >
      {open && (
        <ul className="divide-y divide-line-subtle">
          {rows.map((r) => (
            <li key={r.id} className="flex items-start gap-3 py-2 first:pt-0">
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm text-ink">{r.title}</p>
                <p className="text-2xs text-ink-tertiary">
                  {r.status.replace('_', ' ')} · {r.audience === 'external' ? 'requester-facing' : 'agent-facing'} · deleted{' '}
                  {ago(r.deleted_at)}
                  {r.deleted_by_name ? ` by ${r.deleted_by_name}` : ''}
                  {r.delete_reason ? ` — ${r.delete_reason}` : ''}
                </p>
              </div>
              <Button size="sm" variant="secondary" onClick={() => restore.mutate(r.id)} disabled={restore.isPending}>
                <RotateCcw aria-hidden className="h-3.5 w-3.5" />
                Restore
              </Button>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

/**
 * One row of the efficacy table (FR-KB-53).
 *
 * THE POINT OF THIS ROW IS TO LOOK BAD WHEN THE ARTICLE IS BAD. An article
 * served nine times that produced nine reopens is worse than no article at
 * all, and a plain number next to a plain number does not say that loudly
 * enough for a reader scanning fifty rows to catch it — hence the flag.
 */
/**
 * What each mode is, in the words of somebody reading the table.
 *
 * Rows are grouped by article AND mode, so an article used both ways appears
 * twice — correctly, and indistinguishably until the mode is on the row. Two
 * lines reading "CATO VPN / 152830010" with different numbers is
 * indistinguishable from duplicated data, which is what it was taken for.
 */
const MODE_LABEL: Record<ArticleEfficacy['mode'], string> = {
  auto_response: 'answered a requester',
  suggestion: 'shown to an agent',
};

const MODE_HINT: Record<ArticleEfficacy['mode'], string> = {
  auto_response:
    'The assistant answered from this article before anybody at the desk saw the request. Outcomes are deflected, reopened or escalated.',
  suggestion:
    'An agent working a ticket was shown this article. Outcomes are used or ignored; a deflection is not possible here.',
};

function ArticleEfficacyRow({ a }: { a: ArticleEfficacy }) {
  const decided = a.deflected + a.reopened;
  /**
   * A DASH WHERE THE MEASURE DOES NOT APPLY, not a zero.
   *
   * `deflected`/`reopened`/`escalated` only exist for an auto-response, and
   * `used`/`ignored` only for a suggestion. Printing 0 for the other half
   * says "measured, and it never happened" about something that cannot
   * happen — and invites the reader to add up columns that describe
   * different populations. The table already uses a dash this way for the
   * rate, and says so in its footer.
   */
  const auto = a.mode === 'auto_response';
  const na = <span className="text-ink-tertiary">—</span>;
  // Only raised once there is something decided to be worried about — a
  // fresh article with zero of each is not yet a risk, it is not yet data.
  const risky = decided > 0 && a.reopened >= a.deflected;

  const rateTone =
    a.deflection_rate === null
      ? 'neutral'
      : a.deflection_rate >= 0.7
        ? 'ok'
        : a.deflection_rate >= 0.4
          ? 'warn'
          : 'danger';

  return (
    <TR>
      <TD>
        <CellStack
          primary={a.article_title ?? 'Untitled article'}
          // `article_title` is nullable in the API type; the id is a
          // fallback for the reader, never a guessed title. The mode is
          // always named: it is what tells two rows for one article apart.
          secondary={
            <span title={MODE_HINT[a.mode]}>
              {a.article_title ? `${a.article_id} · ` : ''}
              {MODE_LABEL[a.mode]}
            </span>
          }
        />
      </TD>
      <TD numeric>{num(a.served)}</TD>
      <TD numeric>{auto ? num(a.deflected) : na}</TD>
      <TD numeric>
        <span className="inline-flex items-center justify-end gap-1.5">
          <span className={risky ? 'font-semibold text-danger' : undefined}>
            {auto ? num(a.reopened) : na}
          </span>
          {risky && (
            <Badge
              tone="danger"
              title="Reopens at or above deflections on decided outcomes — this answer is not landing."
            >
              not working
            </Badge>
          )}
        </span>
      </TD>
      <TD numeric>{auto ? num(a.escalated) : na}</TD>
      <TD numeric>{auto ? na : num(a.used)}</TD>
      <TD numeric>{auto ? na : num(a.ignored)}</TD>
      <TD numeric muted title="Still inside the reopen window — no outcome recorded yet.">
        {num(a.pending)}
      </TD>
      <TD numeric>
        <Badge tone={rateTone}>{pct(a.deflection_rate)}</Badge>
      </TD>
    </TR>
  );
}

function PatternRow({
  p,
  tenant,
  onOpen,
  focused = false,
}: {
  p: Pattern;
  tenant: string;
  onOpen: (id: string) => void;
  /** Linked to from a ticket: bring it into view and mark it. */
  focused?: boolean;
}) {
  const row = useRef<HTMLLIElement>(null);
  useEffect(() => {
    if (focused) row.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [focused]);
  const qc = useQueryClient();
  const toast = useToast();
  const [drafting, setDrafting] = useState<Audience | null>(null);

  const draft = useMutation({
    mutationFn: (audience: Audience) =>
      api.post<{ articleId: string; result: { kind: string } }>(
        `/api/v1/tenants/${tenant}/kb/patterns/${p.id}/draft`,
        { audience },
      ),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ['kb-patterns', tenant] });
      toast.success(
        r.result.kind === 'article' ? 'Draft written' : 'Not enough evidence — a brief was written instead',
      );
      onOpen(r.articleId);
    },
    onError: (e: unknown) =>
      toast.error(e instanceof ApiError ? e.message : 'The draft could not be written'),
    onSettled: () => setDrafting(null),
  });

  const auto = Number(p.automated_share);
  const byAudience = (a: Audience) => p.articles.find((x) => x.audience === a);

  return (
    <li
      ref={row}
      className={
        focused
          ? 'rounded-md border border-brand-line bg-brand-tint/40 p-3 ring-1 ring-brand-line'
          : 'rounded-md border border-line-subtle bg-surface-sunken/40 p-3'
      }
    >
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-sm font-medium text-ink">{p.problem}</span>
        {p.subject && <Badge tone="neutral">{p.subject}</Badge>}
        <Badge tone={p.kind === 'request' ? 'info' : 'neutral'}>{p.kind}</Badge>
        {p.trend === 'emerging' && (
          <Tooltip content="Arriving faster than it used to.">
            <span>
              <Badge tone="warn" icon={<TrendingUp />}>
                emerging
              </Badge>
            </span>
          </Tooltip>
        )}
      </div>

      <p className="mt-1 flex flex-wrap items-center gap-x-3 text-2xs text-ink-tertiary">
        <span>
          <strong className="text-ink-secondary">{num(p.tickets)}</strong> tickets
        </span>
        {p.median_elapsed_minutes !== null && (
          <span>median {Math.round(p.median_elapsed_minutes / 60)}h elapsed</span>
        )}
        {auto > 0 && <span>{Math.round(auto * 100)}% already closed with no reply</span>}
        <span>{p.members.length} phrasings merged</span>
      </p>

      <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
        {(['external', 'internal'] as const).map((aud) => {
          const a = byAudience(aud);
          const Icon = aud === 'external' ? BookOpen : Lock;
          return a ? (
            <Button key={aud} variant="secondary" size="sm" onClick={() => onOpen(a.id)}>
              <Icon aria-hidden className="h-3.5 w-3.5" />
              {aud === 'external' ? 'Requester' : 'Agent'}
              <ArticleStatusBadge status={a.status} />
            </Button>
          ) : (
            <Button
              key={aud}
              variant="ghost"
              size="sm"
              loading={drafting === aud}
              disabled={draft.isPending}
              onClick={() => {
                setDrafting(aud);
                draft.mutate(aud);
              }}
            >
              <Sparkles aria-hidden className="h-3.5 w-3.5" />
              Draft for {aud === 'external' ? 'requester' : 'agent'}
            </Button>
          );
        })}
      </div>
    </li>
  );
}

function ArticleReview({
  tenant,
  id,
  perms,
  onClose,
}: {
  tenant: string;
  id: string;
  /**
   * What this person may do to an article. Each control is shown only with
   * its capability — the demo found edit buttons offered to people the API
   * would refuse.
   */
  perms: { author: boolean; review: boolean; publish: boolean };
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const [reason, setReason] = useState('');
  /**
   * Held apart from `reason` on purpose. The review reason and the withdrawal
   * reason are written on different screens of the same dialog and land in
   * different columns; sharing one box means a half-typed rejection note
   * becomes the permanent record of why an article stopped being the answer.
   */
  const [withdrawReason, setWithdrawReason] = useState('');
  /** Delete is two steps: say why, then confirm. */
  const [deleting, setDeleting] = useState(false);
  const [deleteReason, setDeleteReason] = useState('');

  /**
   * The draft being typed, held separately from the saved article.
   *
   * Seeded when edit mode is ENTERED rather than from a default value: the
   * article arrives asynchronously, and an uncontrolled input seeded on first
   * render shows the previous article's text — a bug this codebase has
   * already shipped once on the Platform tab.
   */
  const [editing, setEditing] = useState(false);
  const [draftTitle, setDraftTitle] = useState('');
  const [draftBody, setDraftBody] = useState('');

  const q = useQuery({
    queryKey: ['kb-article', tenant, id],
    queryFn: () => api.get<KbArticleDetail>(`/api/v1/tenants/${tenant}/kb/articles/${id}`),
    // ACTUALLY POLL. The note below has always said this polls while a
    // worker is acting; nothing ever made it. The status is flipped by the
    // worker a second or two after the request returns, and with no refetch
    // the dialog sat on "Publishing" forever — a spinner for work that had
    // already finished, which is the one thing it must never be.
    refetchInterval: (query) =>
      query.state.data && IN_FLIGHT.has(query.state.data.status) ? 2_000 : false,
  });

  /**
   * Only while a worker is supposed to be acting. A spinner that never ends
   * is indistinguishable from one that is about to, and this is the only
   * thing that can tell them apart — so it polls, but only when the answer
   * could change what the reader does.
   */
  const inFlight = q.data !== undefined && IN_FLIGHT.has(q.data.status);
  const health = useQuery({
    queryKey: ['queue-health', tenant],
    queryFn: () => api.get<QueueHealth>(`/api/v1/tenants/${tenant}/ops/queue-health`),
    enabled: inFlight,
    refetchInterval: inFlight ? 15_000 : false,
  });

  const after = () => {
    void qc.invalidateQueries({ queryKey: ['kb-article', tenant, id] });
    void qc.invalidateQueries({ queryKey: ['kb-patterns', tenant] });
  };

  const move = useMutation({
    mutationFn: () => api.post(`/api/v1/tenants/${tenant}/kb/stranded/move`, { id }),
    onSuccess: () => {
      after();
      void qc.invalidateQueries({ queryKey: ['kb-stranded', tenant] });
      toast.success('Moving — the worker will confirm the new collection has it');
    },
    onError: (e: unknown) => toast.error(e instanceof ApiError ? e.message : 'It could not be moved'),
  });

  const act = useMutation({
    mutationFn: (v: { path: string; body: unknown }) =>
      api.post(`/api/v1/tenants/${tenant}/kb/articles/${id}/${v.path}`, v.body),
    onSuccess: (_d, v) => {
      after();
      // NOT "published": the API sets `publishing` and only the worker
      // promotes it, once the index has accepted the document. Saying
      // published here is the exact claim migration 021 exists to stop the
      // system making.
      toast.success(
        v.path === 'publish' ? 'Publishing — the worker will confirm the index has it' : 'Recorded',
      );
      setReason('');
    },
    onError: (e: unknown) =>
      toast.error(e instanceof ApiError ? e.message : 'That could not be recorded'),
  });

  const submit = useMutation({
    mutationFn: () => api.patch(`/api/v1/tenants/${tenant}/kb/articles/${id}`, { status: 'in_review' }),
    onSuccess: () => {
      after();
      toast.success('Sent for review');
    },
  });

  /**
   * Editing the text (FR-KB-57).
   *
   * The API has supported this from the start and the screen never offered
   * it, so a drafted article could be approved or rejected but never
   * corrected — and the empty-brief callout told the reader to "edit it into
   * an article first", which was impossible.
   *
   * An edit always creates a new version and returns the article to `draft`,
   * because approved has to mean approved-as-written. Where that costs
   * something — a live article, or one already approved — the button says so
   * before it is pressed rather than after.
   */
  const save = useMutation({
    mutationFn: () =>
      api.patch(`/api/v1/tenants/${tenant}/kb/articles/${id}`, {
        title: draftTitle.trim(),
        body: draftBody,
      }),
    onSuccess: () => {
      after();
      setEditing(false);
      toast.success('Saved as a new version, back in draft');
    },
    onError: (e: unknown) =>
      toast.error(e instanceof ApiError ? e.message : 'That could not be saved'),
  });

  const [review, setReview] = useState<TextReview | null>(null);
  /** The proposal as the author has it — theirs to add to before accepting. */
  const [proposedBody, setProposedBody] = useState('');
  const [matches, setMatches] = useState<ArticleMatches | null>(null);

  /**
   * Reformat and language-fix, as a PROPOSAL.
   *
   * The server refuses a rewrite that invented anything, so a 422 here is
   * usually the model having been caught rather than a failure to run — and
   * its message says which fabrication, which is worth reading.
   */
  const reviewText = useMutation({
    mutationFn: () =>
      api.post<TextReview>(`/api/v1/tenants/${tenant}/kb/articles/${id}/review-text`, {}),
    onSuccess: (r) => {
      setReview(r);
      setProposedBody(r.proposed.body);
    },
    onError: (e: unknown) =>
      toast.error(e instanceof ApiError ? e.message : 'The review could not be run'),
  });

  /** Accepting is an ordinary edit — same endpoint a hand edit uses. */
  const acceptReview = useMutation({
    mutationFn: () =>
      api.patch(`/api/v1/tenants/${tenant}/kb/articles/${id}`, {
        title: review!.proposed.title,
        // What is on screen, which may include an Applies to the author added.
        body: proposedBody,
      }),
    onSuccess: () => {
      after();
      setReview(null);
      toast.success('Saved as a new version, back in draft');
    },
    onError: (e: unknown) =>
      toast.error(e instanceof ApiError ? e.message : 'That could not be saved'),
  });

  const findMatches = useMutation({
    mutationFn: () =>
      api.post<ArticleMatches>(`/api/v1/tenants/${tenant}/kb/articles/${id}/matches`, {
        // While editing, test the text ON SCREEN rather than what was saved —
        // the whole point is to try a phrasing before committing to it.
        text: editing ? `${draftTitle} ${draftBody}` : undefined,
      }),
    onSuccess: (m) => setMatches(m),
    onError: (e: unknown) =>
      toast.error(e instanceof ApiError ? e.message : 'The search could not be run'),
  });

  /**
   * Change who the article is for.
   *
   * A 409 means it is published: the server refuses rather than stranding
   * the indexed copy in a lane nobody is pointing at any more. Its message
   * says to withdraw first, which is the actual next step.
   */
  const setAudience = useMutation({
    mutationFn: (next: 'internal' | 'external') =>
      api.patch(`/api/v1/tenants/${tenant}/kb/articles/${id}`, { audience: next }),
    onSuccess: () => {
      after();
      toast.success('Audience changed');
    },
    onError: (e: unknown) =>
      toast.error(e instanceof ApiError ? e.message : 'The audience could not be changed'),
  });

  const remove = useMutation({
    mutationFn: () =>
      api.del(`/api/v1/tenants/${tenant}/kb/articles/${id}`, { reason: deleteReason.trim() }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['kb-patterns', tenant] });
      void qc.invalidateQueries({ queryKey: ['kb-deleted', tenant] });
      toast.success('Article deleted', 'A publisher can restore it from Deleted articles at the bottom of this page.');
      onClose();
    },
    onError: (e: unknown) => toast.error('Not deleted', e instanceof ApiError ? e.message : undefined),
  });

  const withdraw = useMutation({
    mutationFn: () =>
      api.post(`/api/v1/tenants/${tenant}/kb/articles/${id}/withdraw`, {
        reason: withdrawReason.trim(),
      }),
    onSuccess: () => {
      after();
      toast.success('Withdrawal queued — the reason is on the record');
      setWithdrawReason('');
    },
    onError: (e: unknown) =>
      toast.error(e instanceof ApiError ? e.message : 'The withdrawal could not be recorded'),
  });

  const a = q.data;
  /**
   * A brief is what the DRAFTING MODEL said it was, not what the citation
   * count implies.
   *
   * This was `a.evidence.length === 0`, which made every hand-written article
   * a brief — somebody writing up a known fix pre-emptively cites no tickets
   * because none exist yet, and the system then refused to publish it with no
   * way to disagree. Citing nothing is a fact about provenance; having no
   * solution is a fact about the text, and only the second should block
   * publication.
   */
  const isBrief = a !== undefined && a.kind === 'brief';

  /**
   * While the worker holds the article, every button is a way to queue a
   * second contradictory job against a state this screen cannot see yet.
   */
  const flight = a !== undefined && IN_FLIGHT.has(a.status);
  const flightTitle =
    a?.status === 'publishing'
      ? 'Publishing is in progress — wait for the index to confirm.'
      : 'Withdrawal is in progress — wait for the index to confirm.';

  // The server rejects anything shorter with a 422; there is no value in
  // letting the button fire just to be told so.
  const reasonEnough = withdrawReason.trim().length >= 3;

  const liveElsewhere =
    a !== undefined && a.published_version !== null && a.published_version !== a.current_version;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        title={a ? a.title : 'Article'}
        description={
          a
            ? `${a.audience === 'external' ? 'Requester-facing' : 'Agent-facing'} · version ${a.current_version} · ${
                a.drafted_by === 'model'
                  ? 'drafted by model'
                  : // Name the author. "Written by a person" is true and
                    // useless: the reader wants to know who to ask.
                    `written by ${a.author_name ?? a.author_email ?? 'a person'}`
              }`
            : ''
        }
        width="lg"
      >
        {!a ? (
          <Spinner />
        ) : (
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-1.5">
              <ArticleStatusBadge status={a.status} />
              {a.published_collection && (
                <Badge tone="ok" icon={<Upload />}>
                  {a.published_collection}
                </Badge>
              )}
            </div>

            {liveElsewhere && (
              <Callout tone="info">
                <strong>
                  Version {a.current_version} &mdash; {a.status.replace('_', ' ')}. Version{' '}
                  {a.published_version} is live and answering requesters.
                </strong>{' '}
                The text below is the newer version and it is not in the index. Nothing changes
                for requesters until it is approved and published.
              </Callout>
            )}

            {/* A SPINNER THAT NEVER ENDS LOOKS EXACTLY LIKE ONE THAT IS ABOUT
                TO. `publishing` can only be left by the worker, which is
                correct — and means a worker that is not running is
                indistinguishable from a slow one. Both times that happened
                here, the outbox row sat at zero attempts and nothing in the
                product said so. This is the only place that can tell the
                reader which of the two they are looking at. */}
            {flight && health.data?.stalled && (
              <Callout tone="warn" icon={<AlertTriangle />} title="The delivery queue is not moving">
                {a.status === 'publishing' ? 'This publish' : 'This withdrawal'} is queued, but
                nothing has been picked up for{' '}
                {Math.round((health.data.oldestPendingSeconds ?? 0) / 60)} minutes
                {health.data.pending > 1 ? `, and ${health.data.pending} items are waiting` : ''}.
                The queue is read every few seconds when the worker is running, so this is almost
                always a worker that is not running rather than one that is slow. Nothing is lost
                — the work resumes where it left off once it is.
              </Callout>
            )}

            {/* WHY IT IS NOT FINISHING (080). The worker's error used to be
                only in its log; the article now carries it, and the queued
                job's own last error shows even for jobs queued before. A job
                the worker gave up on, or one that keeps failing, can be
                retried or cancelled here — cancelling puts the article back
                where it truly is. */}
            {(flight || a.sync?.error) && (a.sync?.error || a.sync?.job?.lastError) && (
              <Callout
                tone={flight ? 'warn' : 'neutral'}
                icon={<AlertTriangle />}
                title={
                  flight
                    ? `${a.status === 'publishing' ? 'Publishing' : 'Withdrawing'} is not going through`
                    : 'The last publish or withdrawal did not go through'
                }
              >
                <p className="break-words">{a.sync?.job?.lastError ?? a.sync?.error}</p>
                {flight && a.sync?.job && (
                  <p className="mt-1 text-2xs text-ink-secondary">
                    {a.sync.job.dead
                      ? `Given up after ${a.sync.job.attempts} attempt${a.sync.job.attempts === 1 ? '' : 's'}.`
                      : `Attempt ${a.sync.job.attempts} so far${a.sync.job.nextAttemptAt ? `; next try ${new Date(a.sync.job.nextAttemptAt).toLocaleTimeString()}` : ''}.`}
                  </p>
                )}
                {flight && perms.publish && (
                  <div className="mt-2 flex gap-1.5">
                    {a.sync?.job?.dead && (
                      <Button size="xs" loading={act.isPending} onClick={() => act.mutate({ path: 'sync/retry', body: {} })}>
                        Retry
                      </Button>
                    )}
                    <Button
                      size="xs"
                      variant="secondary"
                      loading={act.isPending}
                      onClick={() => act.mutate({ path: 'sync/cancel', body: {} })}
                    >
                      {a.status === 'withdrawing' ? 'Cancel withdrawal' : 'Cancel publishing'}
                    </Button>
                  </div>
                )}
              </Callout>
            )}

            {/* LEFT IN A COLLECTION THE DESK NO LONGER USES. Changing a desk's
                collection does not move what it already published, and the
                desk searches only the new one — so this answers nothing while
                saying "published". */}
            {a.status === 'published' && a.stranded && (
              <Callout tone="warn" icon={<AlertTriangle />} title="Published to a collection this desk no longer uses">
                <p>
                  It is in <span className="font-mono">{a.stranded.publishedCollection}</span>, but this desk now
                  publishes to and searches <span className="font-mono">{a.stranded.currentCollection}</span>, so it is
                  not answering anything.
                </p>
                {perms.publish && (
                  <Button
                    size="xs"
                    className="mt-2"
                    loading={move.isPending}
                    onClick={() => move.mutate()}
                  >
                    Move to current collection
                  </Button>
                )}
              </Callout>
            )}

            {/* A withdrawal that was queued, ran, and FAILED puts the article
                back to `published` and writes the reason to `withdrawal_note`
                — the article never leaves `published` at all, so without this
                the failure is invisible and a reader assumes withdrawal was
                simply never asked for. */}
            {a.status === 'published' && a.withdrawal_note && (
              <Callout tone="warn" icon={<AlertTriangle />} title="A withdrawal did not succeed">
                {a.withdrawal_note}
              </Callout>
            )}

            {(a.status === 'withdrawing' || a.status === 'withdrawn') && (
              <Callout
                // Not `accent` when verification could not be confirmed: that
                // tone reads as a settled, final state, and `false` is
                // deliberately not one (FR-KB-58b).
                tone={
                  a.status === 'withdrawing' ? 'warn'
                  : a.withdrawal_verified === false ? 'warn'
                  : 'accent'
                }
                title={
                  a.status === 'withdrawing'
                    ? 'Queued — the index has not confirmed removal'
                    : a.withdrawal_verified === false
                      ? 'Removed — not confirmed'
                      : 'Withdrawn from the index'
                }
              >
                {/* Never call `withdrawing` withdrawn. The worker proves the
                    article is gone before the state changes, and until it
                    does the article may still be answering requesters —
                    which is the only thing this state exists to say. */}
                {a.status === 'withdrawing' && (
                  <p>Treat it as still retrievable until the sweep confirms it is gone.</p>
                )}
                {/* `withdrawal_verified === false`: the sweep was accepted but
                    the index never positively answered not-found — usually
                    because this deployment holds only the push credential.
                    Saying "withdrawn" here without this note would claim a
                    fact nobody actually checked. */}
                {a.status === 'withdrawn' && a.withdrawal_verified === false && (
                  <p>
                    Not confirmed{a.withdrawal_note ? `: ${a.withdrawal_note}` : ''}. Treat it as
                    possibly still retrievable.
                  </p>
                )}
                <p className={a.status === 'withdrawing' ? 'mt-1' : undefined}>
                  {a.withdrawn_reason
                    ? `Reason: ${a.withdrawn_reason}`
                    : 'No reason was recorded.'}
                  {a.withdrawn_at && <> &middot; {dateTime(a.withdrawn_at)}</>}
                </p>
              </Callout>
            )}

            {a.status === 'withdrawn' && !a.current_version_approved && (
              <Callout
                tone="warn"
                icon={<AlertTriangle />}
                title="The text has changed since it was approved"
              >
                Version {a.current_version} carries no approval of its own, so it cannot go
                straight back into the index &mdash; republishing it would put unreviewed text in
                front of requesters. It needs a review of this version first.
              </Callout>
            )}

            {isBrief && (
              <Callout tone="warn" icon={<AlertTriangle />}>
                No ticket recorded how this was fixed, so this is a{' '}
                <strong>demand brief</strong>, not an article — the problem and its size, for a
                person to take forward. It is deliberately not a guess at the solution.
              </Callout>
            )}

            {editing ? (
              <div className="space-y-2">
                <Field label="Title">
                  <Input
                    value={draftTitle}
                    onChange={(e) => setDraftTitle(e.target.value)}
                    placeholder="What a reader is looking for"
                  />
                </Field>
                <p className="text-2xs text-ink-tertiary">
                  {a.published_version !== null && a.published_version === a.current_version
                    ? `Version ${a.current_version} stays live in the index until this edit is approved and published.`
                    : 'Saving creates a new version and returns it to draft.'}
                </p>
                <ArticleSectionsEditor value={draftBody} onChange={setDraftBody} />
                {/* A box per section, as in "Write an article". */}
              </div>
            ) : (
              <div className="max-h-[38vh] overflow-auto rounded-md border border-line-subtle bg-surface-sunken/40 p-3">
                <ArticleSectionsView body={a.body} />
              </div>
            )}

            <div>
              <p className="mb-1.5 text-2xs font-medium uppercase tracking-wide text-ink-tertiary">
                Evidence — the tickets this was written from
              </p>
              {a.evidence.length === 0 ? (
                <p className="text-2xs text-ink-tertiary">
                  None cited. Nothing here is evidenced by a ticket.
                </p>
              ) : (
                <ul className="space-y-0.5">
                  {a.evidence.map((e) => (
                    <li key={e.id} className="truncate text-2xs">
                      <a
                        href={`/t/${tenant}/${e.number}`}
                        className="text-ink-secondary underline-offset-2 hover:underline"
                      >
                        #{e.number}
                      </a>{' '}
                      <span className="text-ink-tertiary">{e.subject}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {a.reviews.length > 0 && (
              <div>
                <p className="mb-1.5 text-2xs font-medium uppercase tracking-wide text-ink-tertiary">
                  Review history
                </p>
                <ul className="space-y-1.5">
                  {a.reviews.map((r, i) => (
                    <li key={i} className="text-2xs text-ink-tertiary">
                      <span className="flex flex-wrap items-center gap-1.5">
                        <Badge tone={r.decision === 'approved' ? 'ok' : 'danger'}>
                          {r.decision.replace('_', ' ')}
                        </Badge>
                        <span className="text-ink-secondary">{r.reviewer ?? 'someone'}</span>
                        {/* WHEN, which this never showed — `at` was fetched
                            and dropped. A review history without dates cannot
                            answer the question it exists for: was this
                            approved before or after the change somebody is
                            asking about. Absolute, because a record is read
                            months later and "3 months ago" is not a date;
                            relative alongside, because that is what makes a
                            list scannable. */}
                        <Tooltip content={ago(r.at)}>
                          <span className="nums tabular-nums">{dateTime(r.at)}</span>
                        </Tooltip>
                      </span>
                      <span className="mt-0.5 block">{r.reason ?? 'no reason given'}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {perms.review && (a.status === 'draft' || a.status === 'in_review') && (
              <Field label="Reason" hint="Required when rejecting or asking for changes.">
                <Textarea
                  rows={2}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="What needs to change, and why"
                />
              </Field>
            )}

            {perms.publish && a.status === 'published' && (
              <Field
                label="Why is this no longer the answer?"
                hint="Required. This is the only record of why it stopped being the answer — whoever later decides whether to bring it back reads this and nothing else."
              >
                <Textarea
                  rows={2}
                  value={withdrawReason}
                  onChange={(e) => setWithdrawReason(e.target.value)}
                  placeholder="What changed, or what turned out to be wrong with it"
                />
              </Field>
            )}

            <div className="flex flex-wrap items-center justify-end gap-2 border-t border-line-subtle pt-3">
              {/* Left of the decisions: the two things that help you MAKE one. */}
              <Button
                variant="ghost"
                className="mr-auto"
                onClick={() => findMatches.mutate()}
                loading={findMatches.isPending}
              >
                <Search aria-hidden className="h-3.5 w-3.5" />
                Matching tickets
              </Button>
              {perms.author && (
                <Button
                  variant="ghost"
                  onClick={() => reviewText.mutate()}
                  loading={reviewText.isPending}
                  title="Reformat into the house structure and fix spelling and grammar. It may not add anything the article does not already say."
                >
                  <Sparkles aria-hidden className="h-3.5 w-3.5" />
                  AI review
                </Button>
              )}

              {editing ? (
                <>
                  <Button variant="ghost" onClick={() => setEditing(false)} disabled={save.isPending}>
                    Cancel
                  </Button>
                  <Button
                    onClick={() => save.mutate()}
                    loading={save.isPending}
                    disabled={draftTitle.trim() === '' || draftBody.trim() === ''}
                  >
                    <Check aria-hidden className="h-3.5 w-3.5" />
                    Save as new version
                  </Button>
                </>
              ) : perms.author ? (
                <Button
                  variant="ghost"
                  className="mr-auto"
                  disabled={flight}
                  title={
                    flight
                      ? flightTitle
                      : a.status === 'approved' || a.status === 'published'
                        ? 'Editing creates a new version and loses the approval on this one.'
                        : undefined
                  }
                  onClick={() => {
                    // Seeded here, from what is on screen. Seeding at render
                    // time would capture whatever had loaded first.
                    setDraftTitle(a.title);
                    setDraftBody(a.body);
                    setEditing(true);
                  }}
                >
                  <FileText aria-hidden className="h-3.5 w-3.5" />
                  Edit text
                </Button>
              ) : null}
              {perms.author && !editing && a.status === 'draft' && (
                <Button
                  onClick={() => submit.mutate()}
                  loading={submit.isPending}
                  disabled={flight}
                  title={flight ? flightTitle : undefined}
                >
                  <Send aria-hidden className="h-3.5 w-3.5" />
                  Send for review
                </Button>
              )}
              {perms.review && !editing && a.status === 'in_review' && (
                <>
                  <Button
                    variant="ghost"
                    disabled={flight || act.isPending || reason.trim() === ''}
                    title={flight ? flightTitle : undefined}
                    onClick={() =>
                      act.mutate({ path: 'review', body: { decision: 'changes_requested', reason } })
                    }
                  >
                    <FileText aria-hidden className="h-3.5 w-3.5" />
                    Request changes
                  </Button>
                  <Button
                    variant="ghost"
                    disabled={flight || act.isPending || reason.trim() === ''}
                    title={flight ? flightTitle : undefined}
                    onClick={() => act.mutate({ path: 'review', body: { decision: 'rejected', reason } })}
                  >
                    <X aria-hidden className="h-3.5 w-3.5" />
                    Reject
                  </Button>
                  <Button
                    disabled={flight || act.isPending || isBrief}
                    // A disabled control has to carry its own reason. This one
                    // said nothing, and the explanation sat sixty pixels below
                    // in the least prominent text on the page — so the button
                    // simply looked broken.
                    title={
                      flight
                        ? flightTitle
                        : isBrief
                          ? 'This is a demand brief, not an article: there is no evidenced solution to publish. Edit it into an article first, or reject it.'
                          : undefined
                    }
                    onClick={() => act.mutate({ path: 'review', body: { decision: 'approved' } })}
                  >
                    <Check aria-hidden className="h-3.5 w-3.5" />
                    Approve
                  </Button>
                </>
              )}
              {perms.publish && !editing && a.status === 'approved' && (
                <Button
                  onClick={() => act.mutate({ path: 'publish', body: {} })}
                  loading={act.isPending}
                  disabled={flight}
                  title={flight ? flightTitle : undefined}
                >
                  <Upload aria-hidden className="h-3.5 w-3.5" />
                  Publish to index
                </Button>
              )}

              {perms.publish && !editing && a.status === 'published' && (
                <Button
                  onClick={() => withdraw.mutate()}
                  loading={withdraw.isPending}
                  disabled={flight || withdraw.isPending || !reasonEnough}
                  title={
                    flight
                      ? flightTitle
                      : reasonEnough
                        ? undefined
                        : 'Write the reason first — at least a few words.'
                  }
                >
                  <EyeOff aria-hidden className="h-3.5 w-3.5" />
                  Withdraw from index
                </Button>
              )}

              {/* Republish is the publish endpoint, not a separate one: it
                  accepts a withdrawn article whose current version still
                  carries its approval. Where it does not, no one-click
                  control is offered at all — see the callout above. */}
              {perms.publish && a.status === 'withdrawn' && a.current_version_approved && (
                <Button
                  onClick={() => act.mutate({ path: 'publish', body: {} })}
                  loading={act.isPending}
                  disabled={flight || act.isPending}
                  title={flight ? flightTitle : undefined}
                >
                  <RotateCcw aria-hidden className="h-3.5 w-3.5" />
                  Republish to index
                </Button>
              )}

              {flight && (
                <Button variant="subtle" loading disabled title={flightTitle}>
                  {a.status === 'publishing' ? 'Publishing' : 'Withdrawing'}
                </Button>
              )}

              {!editing && !deleting && (perms.publish || (perms.author && (a.status === 'draft' || a.status === 'rejected'))) && (
                <Button
                  variant="ghost"
                  onClick={() => setDeleting(true)}
                  disabled={flight || a.status === 'published'}
                  title={
                    a.status === 'published'
                      ? 'It is in the search index — withdraw it first, then delete it.'
                      : flight
                        ? flightTitle
                        : undefined
                  }
                >
                  <Trash2 aria-hidden className="h-3.5 w-3.5" />
                  Delete
                </Button>
              )}

              <Button variant="ghost" onClick={onClose}>
                Close
              </Button>
            </div>

            {deleting && (
              <Callout tone="danger">
                <div className="space-y-2">
                  <p>
                    Delete <strong>{a.title}</strong>? It disappears from Knowledge and every check that looks for an
                    article. Nothing is lost: a publisher can restore it from Deleted articles.
                  </p>
                  <Input
                    value={deleteReason}
                    onChange={(e) => setDeleteReason(e.target.value)}
                    placeholder="Why — e.g. duplicate of another article"
                    aria-label="Why it is being deleted"
                  />
                  <div className="flex gap-2">
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => remove.mutate()}
                      loading={remove.isPending}
                      disabled={deleteReason.trim().length < 3 || remove.isPending}
                      title={deleteReason.trim().length < 3 ? 'Say why first — at least a few words.' : undefined}
                    >
                      Delete article
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => { setDeleting(false); setDeleteReason(''); }}>
                      Cancel
                    </Button>
                  </div>
                </div>
              </Callout>
            )}

            {/* A republish refused for want of an approval comes back as a 422
                whose message is the only explanation of what to do next; a
                toast that has already faded is not a record of it. */}
            {a.status === 'withdrawn' && act.error instanceof ApiError && (
              <Callout tone="danger">{act.error.message}</Callout>
            )}

            {/* THE REVIEW, INLINE.
                It was a Dialog nested inside this Dialog's root, which Radix
                portals out behind the outer modal's focus trap — so it
                rendered and was never visible. A comparison belongs in the
                same surface as the thing being compared anyway. */}
            {review && (
              <div className="rounded-md border border-brand-line bg-surface p-3">
                <div className="mb-2 flex items-center justify-between gap-2">
                  <p className="text-2xs font-semibold uppercase tracking-wide text-ink-tertiary">
                    AI review
                  </p>
                  <Button variant="ghost" size="sm" onClick={() => setReview(null)}>
                    <X aria-hidden className="h-3.5 w-3.5" />
                    Discard
                  </Button>
                </div>
                <p className="mb-2 text-2xs text-ink-tertiary">
                  Reformatted into the house structure, spelling and grammar corrected. Nothing was
                  added — a rewrite that introduced anything the article did not say is refused
                  before it reaches you.
                </p>
            {review.changes.length > 0 && (
              <div className="rounded-md border border-line bg-surface-sunken p-2.5">
                <p className="text-2xs font-semibold uppercase tracking-wide text-ink-tertiary">
                  What changed
                </p>
                <ul className="mt-1 list-disc space-y-0.5 pl-4 text-xs text-ink-secondary">
                  {review.changes.map((c, i) => (
                    <li key={i}>{c}</li>
                  ))}
                </ul>
              </div>
            )}

            {review.missing.length > 0 && (
              <div className="mt-2 rounded-md border border-warn-line bg-warn-tint/30 p-2.5">
                <p className="text-2xs text-ink-secondary">
                  Not covered:{' '}
                  <span className="font-medium text-ink">{review.missing.join(', ')}</span>. Left
                  out rather than guessed — the assistant sends these to requesters as written.
                </p>

                {/* WHY THE MISSING SECTION MATTERS, rather than noting that a
                    template has a hole in it. "Applies to" is where the
                    requester's vocabulary lives, and retrieval has nothing
                    else to match a request against. */}
                {review.missing.includes('Applies to') && (
                  <p className="mt-1.5 text-2xs text-ink-secondary">
                    <strong className="font-medium text-ink">Applies to</strong> is the section
                    that decides whether anyone finds this. An article written in the desk&rsquo;s
                    words — &ldquo;requests an AI license&rdquo; — does not match a request written
                    in the requester&rsquo;s — &ldquo;I need chat gpt on my laptop&rdquo;. Name the
                    systems, and the ways people ask.
                  </p>
                )}

                {review.phrasings.length > 0 && (
                  <div className="mt-2">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-2xs text-ink-tertiary">
                        Real requests this article answers, in their words:
                      </p>
                      {/* The author's act, not the model's. The rewrite may
                          not add anything; a person may, with the evidence
                          in front of them and a button that says so. */}
                      {!/^##\s+Applies to\s*$/m.test(proposedBody) && (
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => setProposedBody(withAppliesTo(proposedBody, review.phrasings))}
                        >
                          <Plus aria-hidden className="h-3.5 w-3.5" />
                          Add as Applies to
                        </Button>
                      )}
                    </div>
                    <ul className="mt-1 space-y-0.5">
                      {review.phrasings.map((p, i) => (
                        <li key={i} className="truncate text-2xs text-ink-secondary">
                          &ldquo;{p}&rdquo;
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {/* NOTHING TO COMPARE. Two identical columns take a minute to
                check and teach the reader to stop checking; "no changes"
                takes a second and is the honest answer. */}
            {proposedBody.trim() === review.original.body.trim() &&
              review.proposed.title.trim() === review.original.title.trim() && (
                <p className="mt-2 text-2xs text-ink-secondary">
                  No wording or formatting changes needed — this is already in the house
                  structure.{review.missing.length > 0 ? ' The gaps above are what is left.' : ''}
                </p>
              )}

            <div className="mt-3 grid gap-3 md:grid-cols-2">
              <div className="min-w-0">
                <p className="mb-1 text-2xs font-semibold uppercase tracking-wide text-ink-tertiary">
                  Now
                </p>
                <pre className="h-72 overflow-auto whitespace-pre-wrap rounded-md border border-line bg-surface p-2 text-2xs leading-relaxed text-ink-secondary">
                  {review.original.title}
                  {'\n\n'}
                  {review.original.body}
                </pre>
              </div>
              <div className="min-w-0">
                <p className="mb-1 text-2xs font-semibold uppercase tracking-wide text-ink-tertiary">
                  Proposed
                </p>
                <pre className="h-72 overflow-auto whitespace-pre-wrap rounded-md border border-brand-line bg-brand-tint/30 p-2 text-2xs leading-relaxed text-ink">
                  {review.proposed.title}
                  {'\n\n'}
                  {proposedBody}
                </pre>
              </div>
            </div>

                <div className="mt-3 flex justify-end">
                  <Button
                    variant="primary"
                    loading={acceptReview.isPending}
                    onClick={() => acceptReview.mutate()}
                  >
                    <Check aria-hidden className="h-3.5 w-3.5" />
                    Accept as new version
                  </Button>
                </div>
              </div>
            )}

            {/* WHO IT IS FOR, changeable — the realisation that an article
                should go to the requester instead almost always arrives
                after it is written, and rewriting it from scratch was the
                only way to act on that. */}
            {a && (a.status === 'draft' || a.status === 'in_review') && (
              <Field
                label="Who is this for?"
                hint={
                  a.audience === 'external'
                    ? 'Goes to the requester who reported the problem. Nothing checks this against the text — do not assume admin rights or anything only the desk can do.'
                    : 'Suggested to an agent working the ticket. Never shown to a requester.'
                }
              >
                <Select
                  value={a.audience}
                  onValueChange={(v) => setAudience.mutate(v as 'internal' | 'external')}
                  disabled={setAudience.isPending}
                  options={[
                    { value: 'internal', label: 'Agent-facing' },
                    { value: 'external', label: 'Requester-facing' },
                  ]}
                  aria-label="Who is this for?"
                />
              </Field>
            )}

            {matches && (
              <div className="rounded-md border border-line bg-surface-sunken p-3">
                <p className="text-2xs font-semibold uppercase tracking-wide text-ink-tertiary">
                  Tickets this article covers
                </p>
                {matches.tickets.length === 0 ? (
                  <p className="mt-1.5 text-xs text-ink-secondary">
                    Nothing matched. Every word in this article is common across the desk, so
                    there is nothing distinctive to search on — try naming the system or the
                    error the way a requester would.
                  </p>
                ) : (
                  <>
                    <p className="mt-1 text-2xs text-ink-tertiary">
                      {matches.total.toLocaleString()} share a distinguishing word · searched on{' '}
                      <span className="text-ink-secondary">{matches.terms.join(', ')}</span>
                    </p>
                    <ul className="mt-2 space-y-1">
                      {matches.tickets.map((t) => (
                        <li key={t.id} className="truncate text-xs">
                          <a
                            href={`/t/${tenant}/${t.number}`}
                            target="_blank"
                            rel="noreferrer"
                            className="text-brand hover:underline"
                          >
                            #{t.number}
                          </a>{' '}
                          <span className="text-ink-secondary">{t.subject}</span>
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </div>
            )}

            {/* Why the primary action is unavailable, said where somebody
                looking at the unavailable action will read it. This was
                `text-2xs text-ink-tertiary` — the quietest style available,
                explaining the loudest thing on the screen. */}
            {isBrief && a.status === 'in_review' && (
              <Callout tone="warn">
                <strong className="font-medium">Approve is unavailable for a brief.</strong> There
                is no evidenced solution to publish — this records the problem and its size, not a
                fix. Use <strong className="font-medium">Edit text</strong> to write it into an
                article, or reject it.
              </Callout>
            )}
          </div>
        )}
      </DialogContent>

    </Dialog>
  );
}

/**
 * Write an article from scratch.
 *
 * Deliberately NOT a publish button. A new article enters the same lifecycle
 * as a mined one -- draft, review, approve, publish -- because the review
 * gate is what keeps a wrong answer away from requesters, and text typed
 * straight from memory has had less scrutiny than text built from evidence,
 * not more. This gets you to a draft; the existing screen takes it the rest
 * of the way.
 */
function NewArticleDialog({
  tenant,
  onClose,
  onCreated,
}: {
  tenant: string;
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [audience, setAudience] = useState<Audience>('internal');

  const create = useMutation({
    mutationFn: () =>
      api.post<{ articleId: string }>(`/api/v1/tenants/${tenant}/kb/articles`, {
        title,
        body,
        audience,
      }),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ['kb-patterns', tenant] });
      toast.success('Draft created', 'Send it for review when it reads right.');
      onCreated(r.articleId);
    },
    onError: (e) => {
      // The server's refusal verbatim: it names the field and says why,
      // and rewording it here would lose that.
      toast.error(e instanceof ApiError ? e.message : 'The article could not be created');
    },
  });

  // Matches the server's floor. Checked here only so the button explains
  // itself before the round trip -- the server still decides.
  const ready = title.trim() !== '' && body.trim().length >= 20;

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        title="Write an article"
        description="Someone asked a question and you know the answer. Write it down before it walks out of the building."
        width="lg"
      >
        <div className="space-y-3">
          <Field label="Title" required>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Unlock a timesheet for editing"
            />
          </Field>

          <Field
            label="Who is this for?"
            hint={
              audience === 'external'
                ? // Nothing at publish time checks this against the text --
                  // the author is the only guard against an internal-tools
                  // step reaching someone who cannot run it.
                  "Goes to the requester who reported the problem, not an agent. Don't assume admin rights, a console, or anything only the desk can do."
                : 'Suggested to an agent working the ticket. Never shown to a requester.'
            }
          >
            <Select
              value={audience}
              onValueChange={(v) => setAudience(v as Audience)}
              options={[
                { value: 'internal', label: 'Agent-facing' },
                { value: 'external', label: 'Requester-facing' },
              ]}
              aria-label="Who is this for?"
            />
          </Field>

          {/* A box per section, not one box and hand-typed headings. */}
          <ArticleSectionsEditor value={body} onChange={setBody} required />
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={() => create.mutate()}
            loading={create.isPending}
            disabled={!ready || create.isPending}
            title={ready ? undefined : 'A title and at least a sentence or two.'}
          >
            Create draft
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Put an "Applies to" section into a proposed body, built from real requests.
 *
 * Placed directly after Symptom (or Question), which is where the house order
 * puts it, and before Steps (or Answer) — an author scanning the article reads
 * what it covers before how to do it.
 *
 * The phrasings are quoted verbatim because they are evidence: these are the
 * subjects people actually wrote, and paraphrasing them into tidier English
 * would undo the entire point, which is that retrieval has to match the
 * untidy version.
 */
function withAppliesTo(body: string, phrasings: readonly string[]): string {
  const section = [
    '## Applies to',
    'Requests for this, however they are worded — for example:',
    ...phrasings.map((p) => `- "${p}"`),
  ].join('\n');

  const steps = body.search(/^##\s+(?:Steps|Answer)\s*$/m);
  if (steps === -1) return `${body.trimEnd()}\n\n${section}\n`;
  return `${body.slice(0, steps).trimEnd()}\n\n${section}\n\n${body.slice(steps)}`;
}

/**
 * Published articles left in a collection this desk no longer uses (080).
 * Shown only when there are some: moving a desk's collection used to strand
 * everything it had published, silently.
 */
function StrandedArticles({ tenant }: { tenant: string }) {
  const qc = useQueryClient();
  const toast = useToast();
  const q = useQuery({
    queryKey: ['kb-stranded', tenant],
    queryFn: () =>
      api.get<{ articles: { id: string; title: string; publishedCollection: string; currentCollection: string }[] }>(
        `/api/v1/tenants/${tenant}/kb/stranded`,
      ),
  });
  const moveAll = useMutation({
    mutationFn: () =>
      api.post<{ results: { id: string; ok: boolean; error?: string }[] }>(`/api/v1/tenants/${tenant}/kb/stranded/move`, {}),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ['kb-stranded', tenant] });
      void qc.invalidateQueries({ queryKey: ['kb-patterns', tenant] });
      const failed = r.results.filter((x) => !x.ok);
      if (failed.length) toast.error(`${failed.length} could not be moved`, failed[0]?.error);
      else toast.success(`Moving ${r.results.length} article${r.results.length === 1 ? '' : 's'}`);
    },
    onError: (e: unknown) => toast.error(e instanceof ApiError ? e.message : 'They could not be moved'),
  });
  const list = q.data?.articles ?? [];
  if (list.length === 0) return null;
  return (
    <Panel
      title="Published to a collection this desk no longer uses"
      subtitle="This desk's collection was changed after these were published. They are not answering anything until they are moved."
      action={
        <Button size="xs" loading={moveAll.isPending} onClick={() => moveAll.mutate()}>
          Move {list.length === 1 ? 'it' : `all ${list.length}`} to the current collection
        </Button>
      }
    >
      <ul className="space-y-1 text-xs">
        {list.map((x) => (
          <li key={x.id}>
            <span className="font-medium text-ink">{x.title}</span>{' '}
            <span className="text-2xs text-ink-tertiary">
              in <span className="font-mono">{x.publishedCollection}</span> → <span className="font-mono">{x.currentCollection}</span>
            </span>
          </li>
        ))}
      </ul>
    </Panel>
  );
}
